import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { faCircle as faCircleOutline } from '@fortawesome/free-regular-svg-icons';
import CollapseCard from '../CollapseCard/CollapseCard';
import Marketing2ActionsModal from '../Marketing2/Marketing2ActionsModal';
import AddOpportunity from '../SalesOpportunitiesAdmin/demographicsForm/AddOpportunity';
import { isSaleOpportunityValid } from '../SalesOpportunitiesAdmin/opportunityValidation';
import { buildStatusOptions } from '../SalesOpportunitiesAdmin/opportunityStatusTags';
import CompanyContext from '../../auth/CompanyContext';
import * as utilities from '../../utilities';
import './Contacts2.css';

const getBlankOpportunity = (contactLinkedID = '', sec = '') => ({
    id: 0,
    waNumber: '',
    name: '',
    date: '',
    typeID: 0,
    statusID: 0,
    stageID: 0,
    sourceID: 0,
    assigneeUserID: '',
    assigneeUserOther: '',
    salesRepUserID: '',
    salesRepUserOther: '',
    active: true,
    sec,
    primaryBusinessName: '',
    notes: '',
    nextSteps: '',
    contactLinkedID,
    opportunityAmount: '',
    maintenanceRequirementStatus: '',
    maintenanceNotes: '',
    maintenanceAcceptanceStatus: '',
    maintenanceEndDate: '',
    commissionNotes: '',
    didConvertToSale: false,
    didConvertToOpportunity: false,
    salesActivityDate: '',
    salesActivityTypeID: 0,
    salesActivityNotes: ''
});

const ContactsMarketingCampaigns2 = ({
    clientToken,
    contactGUID,
    contact,
    companies,
    salesActivityFields,
    isActive,
    onCountChange,
    user
}) => {
    const companyCtx = useContext(CompanyContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState({
        workflows: [],
        activities: [],
        opportunities: []
    });
    const [campaigns, setCampaigns] = useState([]);
    const [opportunityOpen, setOpportunityOpen] = useState(false);
    const [opportunityRecordType, setOpportunityRecordType] = useState('Opportunity');
    const [savingOpportunity, setSavingOpportunity] = useState(false);
    const [loadingOpportunityOptions, setLoadingOpportunityOptions] = useState(false);
    const [opportunityOptions, setOpportunityOptions] = useState({
        types: [],
        stages: [],
        sources: [],
        statuses: [],
        selectedStatuses: [],
        users: []
    });
    const [newOpportunity, setNewOpportunity] = useState(() => getBlankOpportunity(contactGUID));
    const [startCampaignOpen, setStartCampaignOpen] = useState(false);
    const [startingCampaign, setStartingCampaign] = useState(false);
    const [newCampaign, setNewCampaign] = useState({
        campaignID: '',
        secID: '',
        startDate: new Date().toLocaleDateString('en-CA')
    });
    const openActionsModalRef = useRef(null);
    const closeActionsModalRef = useRef(null);

    const contactRecord = useMemo(() => ({
        linkedID: contactGUID,
        active: contact?.active ?? true,
        children: contact?.children || {}
    }), [contact, contactGUID]);

    const contactName = contactRecord.children['Contact Name'] || 'this contact';
    const contactEmail = contactRecord.children['Email Address'] || '';

    const loadData = useCallback(() => {
        if (!contactGUID || !clientToken || !companyCtx.company) return;
        setLoading(true);
        setError('');
        fetch(`/api/contacts/marketingActivities/${contactGUID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (!results.success) throw new Error('Unable to load marketing and campaign activity');
                const nextData = {
                    workflows: results.data?.workflows || [],
                    activities: results.data?.activities || [],
                    opportunities: results.data?.opportunities || []
                };
                setData(nextData);
                if (typeof onCountChange === 'function') {
                    onCountChange(nextData.workflows.length + nextData.activities.length + nextData.opportunities.length);
                }
            })
            .catch(() => {
                setError('Unable to load marketing and campaign activity.');
                utilities.addToast('Unable to load marketing and campaign activity.', 5, 'danger');
            })
            .finally(() => setLoading(false));
    }, [clientToken, companyCtx.company, contactGUID, onCountChange]);

    useEffect(() => {
        if (!isActive) return;
        loadData();
    }, [isActive, loadData]);

    useEffect(() => {
        if (typeof onCountChange === 'function') {
            onCountChange(data.workflows.length + data.activities.length + data.opportunities.length);
        }
    }, [data, onCountChange]);

    const openOpportunityModal = useCallback((recordType) => {
        const associatedCompanies = Array.isArray(companies) ? companies : [];
        if (associatedCompanies.length === 0) {
            utilities.addToast(`Associate this contact with a company before adding a ${recordType.toLowerCase()}.`, 5, 'warning');
            return;
        }

        setOpportunityRecordType(recordType);
        const selectedCompany = associatedCompanies.length === 1 ? associatedCompanies[0] : null;
        setNewOpportunity({
            ...getBlankOpportunity(contactGUID, selectedCompany?.secID || ''),
            primaryBusinessName: selectedCompany?.primaryBusinessName || ''
        });
        setOpportunityOpen(true);
        setLoadingOpportunityOptions(true);

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + clientToken,
            'X-Database': companyCtx.company
        };
        Promise.all([
            fetch('/api/opportunities/', { method: 'GET', headers }).then(results => results.json()),
            fetch('/api/opportunitiesTags/', { method: 'GET', headers }).then(results => results.json()),
            fetch('/api/users/', { method: 'GET', headers }).then(results => results.json())
        ])
            .then(([opportunitiesResult, tagsResult, usersResult]) => {
                if (opportunitiesResult.success === false
                    || !Array.isArray(opportunitiesResult.data)
                    || tagsResult.success === false
                    || !Array.isArray(tagsResult.data)
                    || !Array.isArray(usersResult.data)) {
                    throw new Error(`Unable to load ${recordType.toLowerCase()} options`);
                }
                const options = opportunitiesResult.data || [];
                const statuses = options.filter(item => item.Type === 'status');
                const { selectedStatuses } = buildStatusOptions(statuses, tagsResult.data, recordType);
                setOpportunityOptions({
                    types: options.filter(item => item.Type === 'type'),
                    stages: options.filter(item => item.Type === 'stage'),
                    sources: options.filter(item => item.Type === 'source'),
                    statuses,
                    selectedStatuses,
                    users: usersResult.data || []
                });
            })
            .catch(error => {
                utilities.addToast(error.message || `Unable to load ${recordType.toLowerCase()} options.`, 5, 'danger');
                setOpportunityOpen(false);
            })
            .finally(() => setLoadingOpportunityOptions(false));
    }, [clientToken, companies, companyCtx.company, contactGUID]);

    const changeOpportunity = useCallback((event) => {
        const { checked, name, type, value } = event.target;
        setNewOpportunity(current => ({
            ...current,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const changeOpportunityCompany = useCallback((event) => {
        const sec = event.target.value;
        const selectedCompany = (companies || []).find(company => String(company.secID) === String(sec));
        setNewOpportunity(current => ({
            ...current,
            sec,
            primaryBusinessName: selectedCompany?.primaryBusinessName || ''
        }));
    }, [companies]);

    const createOpportunity = useCallback(async () => {
        if (savingOpportunity || !isSaleOpportunityValid(newOpportunity, {
            requireCompany: true,
            requireContact: true
        })) return;
        setSavingOpportunity(true);

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + clientToken,
            'X-Database': companyCtx.company
        };
        try {
            const response = await fetch('/api/salesOpportunities/', {
                method: 'POST',
                body: JSON.stringify({
                    ...newOpportunity,
                    opportunityAmount: parseFloat(newOpportunity.opportunityAmount) > 0
                        ? parseFloat(newOpportunity.opportunityAmount)
                        : null,
                    maintenanceEndDate: newOpportunity.maintenanceEndDate || null
                }),
                headers
            });
            const result = await response.json();
            if (!result.success) throw new Error(result.message || `Unable to add ${opportunityRecordType.toLowerCase()}`);

            let activityNoteSaved = true;
            if (newOpportunity.salesActivityDate
                && newOpportunity.salesActivityTypeID
                && newOpportunity.salesActivityNotes) {
                const noteResponse = await fetch('/api/salesOpportunitiesNotes/', {
                    method: 'POST',
                    body: JSON.stringify({
                        salesOpportunityID: result.result,
                        date: newOpportunity.salesActivityDate,
                        typeID: newOpportunity.salesActivityTypeID,
                        notes: newOpportunity.salesActivityNotes,
                        user
                    }),
                    headers
                });
                const noteResult = await noteResponse.json();
                activityNoteSaved = Boolean(noteResult.success);
            }

            setOpportunityOpen(false);
            setNewOpportunity(getBlankOpportunity(contactGUID));
            utilities.addToast(
                activityNoteSaved
                    ? `${newOpportunity.name} ${opportunityRecordType.toLowerCase()} added.`
                    : `${newOpportunity.name} ${opportunityRecordType.toLowerCase()} was added, but its activity note could not be saved.`,
                5,
                activityNoteSaved ? 'success' : 'warning'
            );
            loadData();
        } catch (error) {
            utilities.addToast(error.message || `Unable to add ${opportunityRecordType.toLowerCase()}.`, 5, 'danger');
        } finally {
            setSavingOpportunity(false);
        }
    }, [clientToken, companyCtx.company, contactGUID, loadData, newOpportunity, opportunityRecordType, savingOpportunity, user]);

    const loadCampaignOptions = useCallback(() => {
        fetch('/api/marketing2Campaigns/active/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (!results.success) throw new Error('Unable to load campaigns');
                setCampaigns(results.data || []);
            })
            .catch(() => {
                utilities.addToast('Unable to load campaigns.', 5, 'danger');
            });
    }, [clientToken, companyCtx.company]);

    const openStartCampaign = useCallback(() => {
        const associatedCompanies = Array.isArray(companies) ? companies : [];
        if (associatedCompanies.length === 0) {
            utilities.addToast('Associate this contact with a company before starting a campaign.', 5, 'warning');
            return;
        }

        setNewCampaign({
            campaignID: '',
            secID: associatedCompanies.length === 1 ? String(associatedCompanies[0].secID || '') : '',
            startDate: new Date().toLocaleDateString('en-CA')
        });
        setStartCampaignOpen(true);
        loadCampaignOptions();
    }, [companies, loadCampaignOptions]);

    const activeCampaignIDsForCompany = useMemo(() => new Set(
        data.workflows
            .filter(row => String(row.SEC || '') === String(newCampaign.secID || '') && !row.CampaignIsComplete)
            .map(row => String(row.CampaignID))
    ), [data.workflows, newCampaign.secID]);

    const startCampaign = useCallback(() => {
        if (!newCampaign.campaignID || !newCampaign.secID || !newCampaign.startDate || startingCampaign) return;
        setStartingCampaign(true);
        fetch('/api/marketing2CampaignsCompanies/', {
            method: 'POST',
            body: JSON.stringify({
                campaignID: parseInt(newCampaign.campaignID),
                secID: newCampaign.secID,
                order: 10,
                auth0: user,
                contactGUID,
                startDate: newCampaign.startDate,
                endDate: '',
                active: true
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (!results.success) throw new Error(results.message || 'Unable to start campaign');
                setStartCampaignOpen(false);
                utilities.addToast(`Campaign started for ${contactName}.`, 5, 'success');
                loadData();
            })
            .catch(error => {
                utilities.addToast(error.message || 'Unable to start campaign.', 5, 'danger');
            })
            .finally(() => setStartingCampaign(false));
    }, [clientToken, companyCtx.company, contactGUID, contactName, loadData, newCampaign, startingCampaign, user]);

    const markCompanyComponentComplete = useCallback((campaignCompanyID, componentCompanyID, componentID, campaignID, secID, action, auth0, linkedCampaignCompanyIDs) => {
        const linkedIDs = String(linkedCampaignCompanyIDs || '')
            .split(',')
            .map(value => parseInt(value))
            .filter(value => Number.isInteger(value) && value > 0);

        return fetch('/api/marketing2ComponentsCompanies/', {
            method: 'PUT',
            body: JSON.stringify({
                campaignCompanyID,
                componentCompanyID,
                componentID,
                campaignID,
                secID,
                action,
                auth0,
                linkedCampaignCompanyIDs: linkedIDs
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (!results.success) throw new Error('Unable to complete action');
                return fetch('/api/marketing2CampaignsCompanies/' + campaignCompanyID + '/complete', {
                    method: 'PUT',
                    headers: {
                        'Authorization': 'Bearer ' + clientToken,
                        'X-Database': companyCtx.company
                    }
                });
            })
            .then(results => results.json())
            .then(results => {
                if (!results.success) throw new Error(results.error || 'Unable to update campaign completion');
                if (results.campaignComplete) utilities.addToast('Campaign complete', 5, 'success');
                closeActionsModalRef.current?.();
                loadData();
            })
            .catch(error => {
                utilities.addToast(error.message || 'Unable to complete action.', 5, 'danger');
                loadData();
            });
    }, [clientToken, companyCtx.company, loadData]);

    const openCurrentAction = useCallback((workflow, modalActions) => {
        if (!openActionsModalRef.current) return;

        const content = workflow.ComponentContent && typeof workflow.ComponentContent === 'object'
            ? { ...workflow.ComponentContent }
            : {};
        if (workflow.ComponentIsActivity) {
            const existingTarget = Array.isArray(content.to)
                ? content.to.find(recipient => String(recipient.linkedID || '').toLowerCase() === String(contactGUID || '').toLowerCase())
                : null;
            content.to = [existingTarget || { linkedID: contactGUID, name: contactName, email: contactEmail }];
        }

        const action = {
            campaignCompanyID: workflow.CampaignCompanyID,
            componentCompanyID: workflow.ComponentCompanyID,
            componentID: workflow.ComponentID,
            campaignID: workflow.CampaignID,
            secID: workflow.SEC,
            content,
            notes: workflow.ComponentNotes || '',
            isComplete: workflow.ComponentIsComplete,
            componentCategory: workflow.ComponentCategory,
            componentCategoryItemID: workflow.ComponentCategoryItemID,
            componentNotes: workflow.ComponentDefinitionNotes,
            componentDuration: workflow.ComponentDuration,
            componentReminder: workflow.ComponentReminder,
            componentSnooze: workflow.ComponentSnooze,
            componentFlowConditionKey: workflow.ComponentFlowConditionKey,
            componentFlowConditionValue: workflow.ComponentFlowConditionValue,
            componentToID: workflow.ComponentToID,
            componentIsActivity: workflow.ComponentIsActivity,
            componentIsDelay: workflow.ComponentIsDelay,
            componentIsFlow: workflow.ComponentIsFlow,
            componentIsSkip: workflow.ComponentIsSkip,
            auth0: workflow.Auth0,
            linkedCampaignCompanyIDs: workflow.LinkedCampaignCompanyIDs || '',
            contactLinkedIDs: contactGUID
        };

        openActionsModalRef.current(action, modalActions, null, false);
    }, [contactEmail, contactGUID, contactName]);

    const getFlowValueMatching = useCallback((workflow) => {
        const conditionKey = parseInt(workflow.ComponentFlowConditionKey);
        const conditionValue = parseInt(workflow.ComponentFlowConditionValue);
        return (workflow.SystemDemographics || []).some(item => (
            parseInt(item.keyID) === conditionKey && parseInt(item.valueID) === conditionValue
        ));
    }, []);

    const completeWorkflowStep = useCallback((workflow, action) => (
        markCompanyComponentComplete(
            workflow.CampaignCompanyID,
            workflow.ComponentCompanyID,
            workflow.ComponentID,
            workflow.CampaignID,
            workflow.SEC,
            action,
            workflow.Auth0,
            workflow.LinkedCampaignCompanyIDs
        )
    ), [markCompanyComponentComplete]);

    const snoozeWorkflowStep = useCallback((workflow) => {
        return fetch('/api/marketing2ComponentsCompanies/snooze/', {
            method: 'PUT',
            body: JSON.stringify({
                componentCompanyID: workflow.ComponentCompanyID,
                snooze: workflow.ComponentSnooze,
                auth0: user
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (!results.success) throw new Error('Unable to snooze action');
                closeActionsModalRef.current?.();
                loadData();
            })
            .catch(error => {
                utilities.addToast(error.message || 'Unable to snooze action.', 5, 'danger');
            });
    }, [clientToken, companyCtx.company, loadData, user]);

    const renderWorkflowActions = (workflow, hideActions = false) => {
        const isAssignedUser = String(workflow.Auth0 || '').toLowerCase() === String(user || '').toLowerCase();
        const isActiveComponentInstance = isActiveValue(workflow.ComponentCompanyActive);
        if (!isAssignedUser
            || workflow.CampaignIsComplete
            || workflow.ComponentIsComplete
            || !isActiveComponentInstance) return null;

        const endDateEpoch = new Date(String(workflow.ComponentEndTimestamp || '').substring(0, 10)).getTime();
        const now = new Date();
        const todayEpoch = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const isDue = !Number.isNaN(endDateEpoch) && todayEpoch >= endDateEpoch;
        const isFlowValueMatching = getFlowValueMatching(workflow);

        return (
            <>
                {workflow.ComponentIsFlow ? (
                    <Button
                        size={hideActions ? "md" : "sm"}
                        color="primary"
                        className="mr-2 mb-2"
                        onClick={() => completeWorkflowStep(workflow, { isFlowValueMatching })}
                    >
                        Confirm {isFlowValueMatching ? 'Match' : 'Not Matched'}
                    </Button>
                ) : null}
                {workflow.ComponentIsSkip ? (
                    <>
                        <Button
                            size={hideActions ? "md" : "sm"}
                            color="primary"
                            className="mr-2 mb-2"
                            onClick={() => completeWorkflowStep(workflow, { skipResponse: true })}
                        >
                            Yes
                        </Button>
                        <Button
                            size={hideActions ? "md" : "sm"}
                            color="primary"
                            className="mr-2 mb-2"
                            onClick={() => completeWorkflowStep(workflow, { skipResponse: false })}
                        >
                            No
                        </Button>
                    </>
                ) : null}
                {!hideActions ? (
                    <Button
                        size="sm"
                        color="primary"
                        className="mr-2 mb-2"
                        onClick={() => openCurrentAction(workflow, renderWorkflowActions(workflow, true))}
                    >
                        Actions
                    </Button>
                ) : null}
                {!workflow.ComponentIsSkip && !workflow.ComponentIsFlow && isDue ? (
                    <Button
                        size={hideActions ? "md" : "sm"}
                        color="success"
                        className="mr-2 mb-2"
                        onClick={() => completeWorkflowStep(
                            workflow,
                            workflow.ComponentIsActivity ? workflow.ComponentContent : null
                        )}
                    >
                        Done
                    </Button>
                ) : null}
                {workflow.ComponentSnooze > 0 && !workflow.IsSnooze && !workflow.ComponentIsDelay ? (
                    <Button
                        size={hideActions ? "md" : "sm"}
                        color="warning"
                        className="mr-2 mb-2"
                        onClick={() => snoozeWorkflowStep(workflow)}
                    >
                        Snooze {workflow.ComponentSnooze}d
                    </Button>
                ) : null}
            </>
        );
    };

    const workflowsByCampaign = useMemo(() => {
        const map = new Map();
        data.workflows.forEach(row => {
            const key = row.CampaignCompanyID || `campaign-${row.CampaignID}`;
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    CampaignCompanyID: row.CampaignCompanyID,
                    CampaignID: row.CampaignID,
                    CampaignName: row.CampaignName,
                    CampaignTypeLabel: row.CampaignTypeLabel,
                    SEC: row.SEC,
                    PrimaryBusinessName: row.PrimaryBusinessName,
                    CampaignStartDate: row.CampaignStartDate,
                    CampaignEndDate: row.CampaignEndDate,
                    CampaignIsComplete: row.CampaignIsComplete,
                    CampaignActive: row.CampaignActive,
                    Active: row.Active,
                    components: []
                });
            }
            map.get(key).components.push(row);
        });
        return Array.from(map.values()).sort((a, b) => {
            const campaignComparison = String(a.CampaignName || '').localeCompare(
                String(b.CampaignName || ''),
                undefined,
                { sensitivity: 'base', numeric: true }
            );
            if (campaignComparison !== 0) return campaignComparison;

            return String(a.PrimaryBusinessName || a.SEC || '').localeCompare(
                String(b.PrimaryBusinessName || b.SEC || ''),
                undefined,
                { sensitivity: 'base', numeric: true }
            );
        });
    }, [data.workflows]);

    const formatDate = (value, includeTime = false) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        const datePart = date.toLocaleDateString('en-US');
        if (!includeTime) return datePart;
        return `${datePart} ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    };

    const formatMoney = (value) => {
        if (value == null || value === '') return '';
        const numberValue = Number(value);
        if (Number.isNaN(numberValue)) return '';
        return numberValue.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        });
    };

    const getMessagePreview = (message) => {
        if (message == null || message === '') return '';
        if (typeof message === 'string') return message;
        return [message.subject, message.message].filter(Boolean).join(' - ');
    };

    const renderCompanyLink = (primaryBusinessName, sec, activeTab = null) => {
        const label = primaryBusinessName || sec;
        if (!label) return 'No company';
        if (!sec) return label;
        return (
            <Link
                onClick={() => {
                    if (activeTab != null) localStorage.setItem('demographicsActiveTab', activeTab);
                }}
                to={{ pathname: `/demographics/${sec}`, state: {} }}
            >
                {label}
            </Link>
        );
    };

    const renderCampaignContextLink = (campaignName, sec) => {
        if (!campaignName) return '';
        const label = campaignName;
        if (!sec) return label;
        return (
            <Link
                // onClick={() => localStorage.setItem('demographicsActiveTab', 7)}
                to={{ pathname: `/marketing`, state: {} }}
                // to={{ pathname: `/demographics/${sec}`, state: {} }}
            >
                {label}
            </Link>
        );
    };

    const renderOpportunityLink = (opportunity) => {
        if (!opportunity.Name) return '';
        if (!opportunity.SEC) return opportunity.Name;
        const activeTab = String(opportunity.LinkType || '').toLowerCase() === 'sale' ? 4 : 3;
        return (
            <Link
                onClick={() => localStorage.setItem('demographicsActiveTab', activeTab)}
                to={{
                    pathname: `/demographics/${opportunity.SEC}`,
                    state: {
                        salesOpportunityID: opportunity.ID,
                        salesOpportunityType: activeTab === 4 ? 'Sale' : 'Opportunity'
                    }
                }}
            >
                {opportunity.Name}
            </Link>
        );
    };

    const isActiveValue = (value) => value === true || value === 1;

    const renderActiveDot = (active) => (
        <FontAwesomeIcon
            icon={isActiveValue(active) ? faCircle : faCircleOutline}
            className={isActiveValue(active) ? 'iconActive' : 'iconInactive'}
            title={isActiveValue(active) ? 'Active' : 'Inactive'}
        />
    );

    const sales = data.opportunities.filter(item => String(item.LinkType || '').toLowerCase() === 'sale');
    const opportunities = data.opportunities.filter(item => String(item.LinkType || '').toLowerCase() !== 'sale');

    const empty = !loading
        && data.workflows.length === 0
        && data.activities.length === 0
        && data.opportunities.length === 0;

    return (
        <div className="text-left">
            <div className="d-flex justify-content-end mb-3">
                <Button
                    color="primary"
                    className="mr-2"
                    onClick={() => openOpportunityModal('Sale')}
                >
                    New Sale
                </Button>
                <Button
                    color="primary"
                    className="mr-2"
                    onClick={() => openOpportunityModal('Opportunity')}
                >
                    New Opportunity
                </Button>
                <Button
                    color="primary"
                    disabled={!contactRecord.active}
                    title={!contactRecord.active ? 'Reactivate this contact before starting a campaign.' : ''}
                    onClick={openStartCampaign}
                >
                    New Campaign
                </Button>
            </div>
            {loading ? <div className="text-muted mb-3">Loading marketing and campaign activity...</div> : null}
            {error && <Alert color="danger">{error}</Alert>}
            {empty && <Alert color="light">No marketing, campaign, sales, or opportunity records are tied to this contact yet.</Alert>}

            {[
                { title: 'Sales', rows: sales },
                { title: 'Opportunities', rows: opportunities }
            ].filter(section => section.rows.length > 0).map(section => (
                <CollapseCard key={section.title} title={`${section.title} (${section.rows.length})`} open={true} header="h4">
                    <Table size="sm">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Date</th>
                                <th>Name</th>
                                <th>WA#</th>
                                <th>Company</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Stage</th>
                                <th>Amount</th>
                                <th>Assignee</th>
                            </tr>
                        </thead>
                        <tbody>
                            {section.rows.map(opportunity => (
                                <tr key={opportunity.ID}>
                                    <td>{renderActiveDot(opportunity.Active)}</td>
                                    <td>{formatDate(opportunity.Date)}</td>
                                    <td>{renderOpportunityLink(opportunity)}</td>
                                    <td>{opportunity['WA#'] || `WA${opportunity.ID}`}</td>
                                    <td>{renderCompanyLink(opportunity.PrimaryBusinessName, opportunity.SEC)}</td>
                                    <td>{opportunity.Type}</td>
                                    <td>{opportunity.Status}</td>
                                    <td>{opportunity.Stage}</td>
                                    <td>{formatMoney(opportunity.OpportunityAmount)}</td>
                                    <td>{opportunity.AssigneeName}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </CollapseCard>
            ))}
            {data.activities.length > 0 && (
                <CollapseCard title={`Marketing Activities (${data.activities.length})`} open={true} header="h4">
                    <Table size="sm">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Date</th>
                                <th>Category</th>
                                <th>Activity</th>
                                <th>Campaign</th>
                                <th>Message</th>
                                <th>Sent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.activities.map(activity => (
                                <tr key={activity.ID}>
                                    <td>{renderActiveDot(activity.Active)}</td>
                                    <td>{formatDate(activity.Timestamp, true)}</td>
                                    <td>{activity.Category}</td>
                                    <td>{activity.Content?.Name || ''}</td>
                                    <td>{renderCampaignContextLink(activity.CampaignName, activity.SEC)}</td>
                                    <td>{getMessagePreview(activity.Message)}</td>
                                    <td>{formatDate(activity.SentTimestamp, true)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </CollapseCard>
            )}
            {data.workflows.length > 0 && (
                <CollapseCard title={`Campaigns (${data.workflows.length})`} open={true} header="h4">
                    {workflowsByCampaign.map(campaign => (
                        <div key={campaign.key} className="mb-3">
                            <div className="font-weight-bold">
                                {renderCampaignContextLink(campaign.CampaignName || 'Campaign', campaign.SEC)}
                                {/* {campaign.CampaignTypeLabel ? <Badge color="secondary" className="ml-2">{campaign.CampaignTypeLabel}</Badge> : null} */}
                                {campaign.CampaignIsComplete
                                    ? <Badge color="success" className="ml-2">Complete</Badge>
                                    : isActiveValue(campaign.CampaignActive)
                                        ? <Badge color="primary" className="ml-2">Active</Badge>
                                        : <Badge color="secondary" className="ml-2">Inactive</Badge>}
                            </div>
                            <div className="text-muted mb-2">
                                {renderCompanyLink(campaign.PrimaryBusinessName, campaign.SEC, 7)}
                            </div>
                            <Table size="sm">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Action</th>
                                        <th>Type</th>
                                        <th>Assignee</th>
                                        <th>Completed/Due</th>
                                        <th>Contact</th>
                                        <th>Notes</th>
                                        <th width="200">&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaign.components.map(component => (
                                        <tr key={component.ComponentCompanyID}>
                                            <td>{renderActiveDot(component.Active)}</td>
                                            <td>{component.ComponentTitle}</td>
                                            <td>{component.ComponentType}</td>
                                            <td>{component.AssigneeName}</td>
                                            <td>{formatDate(component.ComponentEndTimestamp, true)}</td>
                                            <td>
                                                {component.ContactName}
                                                {component.ContactEmail ? <div><a href={`mailto:${component.ContactEmail}`}>{component.ContactEmail}</a></div> : null}
                                            </td>
                                            <td>{component.ComponentNotes ? 'Yes' : ''}</td>
                                            <td className="text-right">
                                                {renderWorkflowActions(component)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ))}
                </CollapseCard>
            )}
            <Modal
                centered
                size="xl"
                isOpen={opportunityOpen}
                toggle={() => !savingOpportunity && setOpportunityOpen(false)}
            >
                <ModalHeader toggle={() => !savingOpportunity && setOpportunityOpen(false)}>
                    New {opportunityRecordType} for {contactName}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="contactOpportunityCompany">Company</Label>
                        <Input
                            id="contactOpportunityCompany"
                            type="select"
                            value={newOpportunity.sec}
                            disabled={savingOpportunity}
                            onChange={changeOpportunityCompany}
                        >
                            <option value="">Select Company</option>
                            {(companies || []).map(company => (
                                <option key={company.secID} value={company.secID}>
                                    {company.primaryBusinessName || company.secID}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                    {loadingOpportunityOptions ? (
                        <div className="text-muted">Loading sale and opportunity options...</div>
                    ) : (
                        <AddOpportunity
                            embedded={true}
                            lockContact={true}
                            requireCompany={true}
                            requireContact={true}
                            saving={savingOpportunity}
                            type={opportunityRecordType}
                            types={opportunityOptions.types}
                            stages={opportunityOptions.stages}
                            sources={opportunityOptions.sources}
                            statuses={opportunityOptions.statuses}
                            selectedStatuses={opportunityOptions.selectedStatuses}
                            users={opportunityOptions.users}
                            contacts={[contactRecord]}
                            opportunities={[]}
                            newOpportunity={newOpportunity}
                            addCategoryCommentsArray={salesActivityFields || []}
                            onChange={changeOpportunity}
                            handleAdd={createOpportunity}
                            handleCancel={() => {
                                setOpportunityOpen(false);
                                setNewOpportunity(getBlankOpportunity(contactGUID));
                            }}
                        />
                    )}
                </ModalBody>
            </Modal>
            <Modal centered isOpen={startCampaignOpen} toggle={() => setStartCampaignOpen(false)}>
                <ModalHeader toggle={() => setStartCampaignOpen(false)}>New Campaign for {contactName}</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="contactCampaignCompany">Company</Label>
                        <Input
                            id="contactCampaignCompany"
                            type="select"
                            value={newCampaign.secID}
                            onChange={(event) => setNewCampaign(current => ({
                                ...current,
                                secID: event.target.value,
                                campaignID: ''
                            }))}
                        >
                            <option value="">Select Company</option>
                            {(companies || []).map(company => (
                                <option key={company.secID} value={company.secID}>
                                    {company.primaryBusinessName || company.secID}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                    <FormGroup>
                        <Label for="contactCampaign">Campaign</Label>
                        <Input
                            id="contactCampaign"
                            type="select"
                            value={newCampaign.campaignID}
                            disabled={!newCampaign.secID}
                            onChange={(event) => setNewCampaign(current => ({ ...current, campaignID: event.target.value }))}
                        >
                            <option value="">Select Campaign</option>
                            {campaigns.map(campaign => (
                                <option
                                    key={campaign.ID}
                                    value={campaign.ID}
                                    disabled={activeCampaignIDsForCompany.has(String(campaign.ID))}
                                >
                                    {campaign.Campaign}{activeCampaignIDsForCompany.has(String(campaign.ID)) ? ' (already active for this contact)' : ''}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                    <FormGroup>
                        <Label for="contactCampaignStartDate">Start Date</Label>
                        <Input
                            id="contactCampaignStartDate"
                            type="date"
                            value={newCampaign.startDate}
                            onChange={(event) => setNewCampaign(current => ({ ...current, startDate: event.target.value }))}
                        />
                    </FormGroup>
                    {newCampaign.secID && newCampaign.campaignID ? (
                        <Alert color="light" className="mb-0">
                            This campaign belongs to the selected company and will target <strong>{contactName}</strong>.
                        </Alert>
                    ) : null}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setStartCampaignOpen(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        disabled={!newCampaign.campaignID || !newCampaign.secID || !newCampaign.startDate || startingCampaign}
                        onClick={startCampaign}
                    >
                        {startingCampaign ? 'Creating...' : 'Create Campaign'}
                    </Button>
                </ModalFooter>
            </Modal>
            <Marketing2ActionsModal
                clientToken={clientToken}
                user={user}
                reload={loadData}
                markCompanyComponentComplete={markCompanyComponentComplete}
                setOpenActionsModal={(fn) => { openActionsModalRef.current = fn; }}
                setCloseActionsModal={(fn) => { closeActionsModalRef.current = fn; }}
            />
        </div>
    );
};

export default ContactsMarketingCampaigns2;
